import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  Keyboard,
  Modal,
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  View,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { offlineSimulatorVisibleAtom } from 'shared/state/SandboxAtoms';
import theme from 'shared/styles/theme';
import {
  ERROR_TYPE_LABELS,
  getNetworkErrorStatus,
  notifyErrorModeChange,
  setNetworkError,
  SimulatedErrorType,
} from 'shared/utils/offlineSimulator';

const SCREEN_WIDTH = Dimensions.get('window').width;
const SCREEN_HEIGHT = Dimensions.get('window').height;
const FAB_SIZE = 56;
const DRAG_THRESHOLD = 5;
const INITIAL_POSITION = {
  x: SCREEN_WIDTH - FAB_SIZE - 24,
  y: SCREEN_HEIGHT - FAB_SIZE - 120,
};

const ERROR_TYPES: SimulatedErrorType[] = [
  'none',
  'network',
  'server',
  'clientError',
  'rateLimit',
  'unauthorized',
  'forbidden',
  'timeout',
  'clientTimeout',
  'graphql',
];

/**
 * Dev-only draggable FAB for toggling simulated network error conditions.
 * Talks to the Metro network error simulator (scripts/networkErrorSimulator.js).
 *
 * "Fail next request only" arms the selected error for a single request —
 * useful for verifying retry flows (first attempt fails, retry succeeds).
 */
const OfflineModeToggle = () => {
  const isVisible = useAtomValue(offlineSimulatorVisibleAtom);
  const [currentError, setCurrentError] = useState<SimulatedErrorType | null>(
    null,
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFailOnceEnabled, setIsFailOnceEnabled] = useState(false);
  const pan = useRef(new Animated.ValueXY(INITIAL_POSITION)).current;
  const panPositionRef = useRef(INITIAL_POSITION);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const listenerId = pan.addListener((position) => {
      panPositionRef.current = position;
    });
    return () => pan.removeListener(listenerId);
  }, [pan]);

  useEffect(() => {
    const fetchStatus = async () => {
      const errorStatus = await getNetworkErrorStatus();
      setCurrentError(errorStatus);
    };
    void fetchStatus();
  }, []);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gestureState) =>
        Math.abs(gestureState.dx) > DRAG_THRESHOLD ||
        Math.abs(gestureState.dy) > DRAG_THRESHOLD,
      onPanResponderGrant: () => {
        isDraggingRef.current = false;
        pan.setOffset(panPositionRef.current);
        pan.setValue({ x: 0, y: 0 });
      },
      onPanResponderMove: (event, gestureState) => {
        if (
          Math.abs(gestureState.dx) > DRAG_THRESHOLD ||
          Math.abs(gestureState.dy) > DRAG_THRESHOLD
        ) {
          isDraggingRef.current = true;
        }
        Animated.event([null, { dx: pan.x, dy: pan.y }], {
          useNativeDriver: false,
        })(event, gestureState);
      },
      onPanResponderRelease: () => {
        pan.flattenOffset();

        const boundedX = Math.max(
          0,
          Math.min(panPositionRef.current.x, SCREEN_WIDTH - FAB_SIZE),
        );
        const boundedY = Math.max(
          0,
          Math.min(panPositionRef.current.y, SCREEN_HEIGHT - FAB_SIZE),
        );

        Animated.spring(pan, {
          toValue: { x: boundedX, y: boundedY },
          useNativeDriver: false,
          friction: 7,
        }).start();

        if (!isDraggingRef.current) {
          setIsExpanded((wasExpanded) => !wasExpanded);
          Keyboard.dismiss();
        }
        isDraggingRef.current = false;
      },
    }),
  ).current;

  const handleErrorSelect = useCallback(
    async (errorType: SimulatedErrorType) => {
      const result = await setNetworkError(errorType, {
        once: isFailOnceEnabled && errorType !== 'none',
      });
      setCurrentError(result);
      setIsExpanded(false);
      notifyErrorModeChange();
    },
    [isFailOnceEnabled],
  );

  if (!isVisible) {
    return null;
  }

  const isSimulationActive = currentError !== null && currentError !== 'none';
  const statusColor = isSimulationActive
    ? theme.color.status.negative
    : theme.color.status.positive;

  return (
    <View style={styles.container} pointerEvents="box-none">
      <Modal
        transparent
        visible={isExpanded}
        animationType="fade"
        onRequestClose={() => setIsExpanded(false)}
        statusBarTranslucent
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setIsExpanded(false)}
        >
          <View style={styles.menuContainer}>
            <BodySmText style={styles.menuTitle}>
              Network Error Simulation
            </BodySmText>
            <Pressable
              style={styles.menuItem}
              onPress={() => setIsFailOnceEnabled(!isFailOnceEnabled)}
            >
              <BodySmText style={styles.menuItemText}>
                Fail next request only
              </BodySmText>
              <Switch
                value={isFailOnceEnabled}
                onValueChange={setIsFailOnceEnabled}
              />
            </Pressable>
            <ScrollView style={styles.menuScrollView}>
              {ERROR_TYPES.map((errorType) => {
                const isSelected = currentError === errorType;
                return (
                  <Pressable
                    key={errorType}
                    style={
                      isSelected
                        ? [styles.menuItem, styles.menuItemSelected]
                        : styles.menuItem
                    }
                    onPress={() => void handleErrorSelect(errorType)}
                  >
                    <BodySmText
                      style={
                        isSelected
                          ? [styles.menuItemText, styles.menuItemTextSelected]
                          : styles.menuItemText
                      }
                    >
                      {ERROR_TYPE_LABELS[errorType]}
                    </BodySmText>
                    {isSelected ? (
                      <Icon
                        iconKey="CHECKMARK"
                        iconSize="SM"
                        iconColor={theme.color.expressive.orchid}
                      />
                    ) : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>

      <Animated.View
        {...panResponder.panHandlers}
        style={[
          styles.fab,
          { transform: [{ translateX: pan.x }, { translateY: pan.y }] },
        ]}
      >
        <Icon
          iconKey="SETTINGS"
          iconSize="MD"
          iconColor={theme.color.background.primary}
        />
        <View
          style={[styles.statusIndicator, { backgroundColor: statusColor }]}
        />
      </Animated.View>

      {isSimulationActive ? (
        <Animated.View
          style={[
            styles.statusBadge,
            { transform: [{ translateX: pan.x }, { translateY: pan.y }] },
          ]}
        >
          <BodyXSText style={styles.statusBadgeText}>
            {ERROR_TYPE_LABELS[currentError]}
          </BodyXSText>
        </Animated.View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  fab: {
    alignItems: 'center',
    backgroundColor: theme.color.status.warning,
    borderRadius: FAB_SIZE / 2,
    elevation: 8,
    height: FAB_SIZE,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    shadowColor: '#000',
    shadowOffset: {
      height: 4,
      width: 0,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    top: 0,
    width: FAB_SIZE,
  },
  menuContainer: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
    bottom: 90,
    elevation: 16,
    maxHeight: 500,
    position: 'absolute',
    right: 24,
    shadowColor: '#000',
    shadowOffset: {
      height: 8,
      width: 0,
    },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    width: 280,
  },
  menuItem: {
    alignItems: 'center',
    borderBottomColor: theme.color.border.primary,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing.s,
  },
  menuItemSelected: {
    backgroundColor: theme.color.background.secondary,
  },
  menuItemText: {
    color: theme.color.text.primary,
    flex: 1,
  },
  menuItemTextSelected: {
    fontWeight: '600',
  },
  menuScrollView: {
    maxHeight: 380,
  },
  menuTitle: {
    borderBottomColor: theme.color.border.primary,
    borderBottomWidth: 1,
    color: theme.color.text.primary,
    fontWeight: '700',
    padding: theme.spacing.s,
  },
  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    flex: 1,
  },
  statusBadge: {
    backgroundColor: theme.color.status.negative,
    borderRadius: 12,
    left: 0,
    marginTop: -32,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: theme.spacing.xs,
    position: 'absolute',
    top: 0,
  },
  statusBadgeText: {
    color: theme.color.background.primary,
    fontWeight: '600',
  },
  statusIndicator: {
    borderColor: theme.color.background.primary,
    borderRadius: 6,
    borderWidth: 2,
    height: 12,
    position: 'absolute',
    right: 4,
    top: 4,
    width: 12,
  },
});

export default OfflineModeToggle;
